import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { formatShopifyPrice } from "../lib/shopify";

const RARITY_STYLES = {
    Esclusivo: "bg-bounty text-parchment",
    "Pre-ordine": "bg-gold text-ink",
    Raro: "bg-ink text-parchment",
    Comune: "bg-seafoam text-ink",
};

export default function ProductCard({ product, reviewSummary }) {
    const isPremium = ["Esclusivo", "Pre-ordine"].includes(product.rarity);
    const url = `/figura/${product.handle}`;
    const hasReviews = reviewSummary && reviewSummary.total > 0;

    return (
        <Link
            to={url}
            className="block"
            data-testid={`product-card-${product.handle || product.id}`}
        >
            <motion.div
                whileHover={{ y: -6, rotate: -1 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 300, damping: 22 }}
                className="bounty-card relative group cursor-crosshair"
            >
                {/* Rarity badge */}
                <span
                    className={`absolute top-3 left-3 z-10 px-3 py-1 border-2 border-ink font-hand text-base rotate-stamp ${RARITY_STYLES[product.rarity] || "bg-seafoam text-ink"}`}
                >
                    {product.rarity}
                </span>

                {!product.available && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-ink/40 pointer-events-none">
                        <span className="bg-ink text-parchment px-4 py-1 font-display tracking-widest -rotate-6 border-2 border-parchment">
                            SOLD OUT
                        </span>
                    </div>
                )}

                {/* Image */}
                <div className="relative aspect-[4/5] overflow-hidden border-b-2 border-ink bg-seafoam">
                    <div className="absolute inset-0 halftone opacity-30 group-hover:opacity-60 transition-manga" />
                    <img
                        src={product.image}
                        alt={product.character}
                        className="w-full h-full object-cover relative z-[1] transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                    />
                    {isPremium && (
                        <div className="absolute inset-0 shimmer-gold pointer-events-none opacity-70" />
                    )}
                </div>

                {/* Body */}
                <div className="p-4 relative">
                    <div className="font-mono text-[10px] tracking-widest uppercase text-ink/60">
                        {product.crew}
                    </div>
                    <div className="font-display text-2xl leading-tight mt-1 tracking-tight line-clamp-2">
                        {product.character}
                    </div>

                    {hasReviews && (
                        <div
                            className="mt-1.5 flex items-center gap-1 text-xs text-ink/70"
                            data-testid={`product-rating-${product.handle}`}
                        >
                            <Star size={12} className="fill-bounty text-bounty" />
                            <span className="font-mono font-bold">
                                {reviewSummary.average.toFixed(1)}
                            </span>
                            <span className="text-ink/50">
                                ({reviewSummary.total})
                            </span>
                        </div>
                    )}

                    <div className="mt-4 flex items-end justify-between">
                        <div>
                            <div className="font-hand text-bounty text-lg leading-none">
                                taglia
                            </div>
                            <div className="font-mono text-xl tracking-tight font-bold">
                                {formatShopifyPrice(product.price, product.currency)}
                            </div>
                        </div>
                        <div className="font-mono text-[10px] text-ink/60 text-right">
                            h. {product.height_cm}cm
                            <br />
                            scala {product.scale}
                        </div>
                    </div>
                </div>
            </motion.div>
        </Link>
    );
}
